import { DateTime } from 'luxon';
import { useCallback, useState } from 'react';
import { FormattedMessage, useIntl } from 'react-intl';
import { useParams } from 'react-router-dom-v5-compat';

import { Button } from 'ui-components/components/button';
import { THEME_CONSTANTS } from 'ui-components/services/button-theme-service';
import { NotificationService, NOTIFICATION_TYPE_ERROR } from 'ui-components/services/notification-service';

import style from '../../../../../components/grid-header/reusable-grid-button.module.less';
import { useInventoryReorderingFeatureGate } from '../../../../../feature-gates/use-inventory-reordering-feature-gate';
import { useLuxonUserDateFormat } from '../../../../../hooks/use-datetime-formats';
import { useModal } from '../../../../../hooks/use-modal';
import { locationNameFromParts } from '../../../../../products/product-details/display-helpers';
import { getInventoryLevelsByWarehouse } from '../../../../../store/inventory/inventory.api';
import { bulkLoadProducts } from '../../../../../store/products/products.api';
import { CreateTransferOrderDrawer } from '../../../transfer-orders/components/create-transfer-order-drawer/create-transfer-order-drawer';
import { useInventoryContext } from '../../inventory-context';
import { messages } from './messages';
import { SelectOriginWarehouseModal } from './select-origin-warehouse-modal';

import type {
  CreateTransferOrderFormData,
  Product,
  ProductLocation,
} from '../../../transfer-orders/components/create-transfer-order-drawer/types';
import type { InventoryLevelLocation } from '../../../transfer-orders/types';

type BulkLoadProduct = {
  productId: number;
  name: string;
  sku: string;
  thumbnailUrl?: string;
};

export const CreateTransferOrderButton = () => {
  const intl = useIntl();
  const dateFormat = useLuxonUserDateFormat();
  const { inventoryWarehouseId } = useParams();
  const { selectedProductIds } = useInventoryContext();
  const { isFeatureEnabled: isInventoryReorderingEnabled } = useInventoryReorderingFeatureGate();
  const [isLoading, setIsLoading] = useState(false);
  const [initialFormData, setInitialFormData] = useState<CreateTransferOrderFormData | undefined>(undefined);

  const { isOpen: isDrawerOpen, open: openDrawer, close: closeDrawer } = useModal();
  const { isOpen: isWarehouseModalOpen, open: openWarehouseModal, close: closeWarehouseModal } = useModal();

  const fetchInventoryAndOpenDrawer = useCallback(
    async (warehouseId: string) => {
      setIsLoading(true);

      try {
        const uniqueProductIds = [...new Set(selectedProductIds)] as string[];

        const [inventoryResponse, bulkLoadResponse] = await Promise.all([
          getInventoryLevelsByWarehouse(uniqueProductIds, warehouseId).promise,
          bulkLoadProducts(uniqueProductIds.map(Number)),
        ]);

        const inventoryLevels: InventoryLevelLocation[] = inventoryResponse?.inventoryLevels ?? [];
        const bulkLoadedProducts: BulkLoadProduct[] = bulkLoadResponse?.products ?? [];

        const products = bulkLoadedProducts
          .map((bulkProduct) => {
            const productId = String(bulkProduct.productId);
            const productInventoryLevels = inventoryLevels.filter((loc) => String(loc.productId) === productId);

            let locations: ProductLocation[] = productInventoryLevels
              .filter((loc) => loc.inventoryLocationId)
              .map((loc) => {
                const lotPart = loc.lotUuid ? `-${loc.lotUuid}` : '';
                const rowId = `${productId}-${loc.inventoryLocationId}${lotPart}`;

                return {
                  rowId,
                  locationId: String(loc.inventoryLocationId),
                  location: locationNameFromParts(loc),
                  onHand: loc.stock ?? 0,
                  available: loc.available ?? 0,
                  quantity: 0,
                  lotId: loc.lot,
                  lotUuid: loc.lotUuid,
                  expirationDate: loc.expirationDate,
                };
              });

            if (locations.length === 0) {
              locations = [
                {
                  rowId: `${productId}-no-inventory`,
                  locationId: '',
                  location: '',
                  onHand: 0,
                  available: 0,
                  quantity: 0,
                  hasNoInventoryInWarehouse: true,
                },
              ];
            }

            return {
              productId,
              name: bulkProduct.name,
              sku: bulkProduct.sku,
              ...(bulkProduct.thumbnailUrl && { thumbnailUrl: bulkProduct.thumbnailUrl }),
              locations,
            };
          })
          .filter((product): product is Product => product !== null);

        if (products.length === 0) {
          NotificationService.showNotification({
            notificationType: NOTIFICATION_TYPE_ERROR,
            title: intl.formatMessage(messages.noProductsForTransferOrder),
          });
          setIsLoading(false);
          return;
        }

        const formData: CreateTransferOrderFormData = {
          originWarehouseId: warehouseId,
          destinationWarehouseId: '',
          orderDate: DateTime.now().toFormat(dateFormat),
          expectedDeliveryDate: '',
          referenceNumber: '',
          notes: '',
          products,
        };

        setInitialFormData(formData);
        setIsLoading(false);
        openDrawer();
      } catch {
        NotificationService.showNotification({
          notificationType: NOTIFICATION_TYPE_ERROR,
          title: intl.formatMessage(messages.failedToLoadInventory),
        });
        setIsLoading(false);
      }
    },
    [selectedProductIds, dateFormat, intl, openDrawer],
  );

  const handleClick = useCallback(() => {
    if (inventoryWarehouseId) {
      fetchInventoryAndOpenDrawer(inventoryWarehouseId);
    } else {
      openWarehouseModal();
    }
  }, [inventoryWarehouseId, fetchInventoryAndOpenDrawer, openWarehouseModal]);

  const handleWarehouseSelected = useCallback(
    (warehouseId: string) => {
      closeWarehouseModal();
      fetchInventoryAndOpenDrawer(warehouseId);
    },
    [closeWarehouseModal, fetchInventoryAndOpenDrawer],
  );

  const handleDrawerClose = useCallback(() => {
    closeDrawer();
    setInitialFormData(undefined);
  }, [closeDrawer]);

  const isDisabled = selectedProductIds.length === 0 || isLoading || !isInventoryReorderingEnabled;

  let disabledTooltipMessage;
  if (!isInventoryReorderingEnabled) {
    disabledTooltipMessage = intl.formatMessage(messages.createTransferOrderButtonFeatureDisabledTooltip);
  } else if (selectedProductIds.length === 0 && !isLoading) {
    disabledTooltipMessage = intl.formatMessage(messages.createTransferOrderButtonDisabledTooltip);
  }

  return (
    <>
      <Button
        className={style['reusable-button']}
        disabled={isDisabled}
        disabledTooltipMessage={disabledTooltipMessage}
        onClick={handleClick}
        theme={THEME_CONSTANTS.tertiary}
      >
        <FormattedMessage {...messages.createTransferOrderButton} />
      </Button>

      {isWarehouseModalOpen ? <SelectOriginWarehouseModal onClose={closeWarehouseModal} onConfirm={handleWarehouseSelected} /> : null}

      {isDrawerOpen && initialFormData ? <CreateTransferOrderDrawer initialFormData={initialFormData} onClose={handleDrawerClose} /> : null}
    </>
  );
};
